import { cx } from '@emotion/css';
import { animated, config, useSpring } from '@react-spring/web';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';

import Overlay from './Overlay';
import {
  containerClassName,
  containerScrollableStyle,
  modalClassName,
  modalScrollableStyle,
} from './styles';
import { ModalProps } from './types';
import { getHeight } from './utils';

type Props = ModalProps;

const Modal = ({
  children,
  className = '',
  closeModal,
  isScrollable,
  isVisible,
}: Props) => {
  const [isV, setV] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [{ y, opacity }, api] = useSpring(() => ({
    y: getHeight(),
    opacity: 0,
  }));

  const open = useCallback(() => {
    api.start({
      y: 0,
      immediate: false,
      config: isMobile
        ? { ...config.stiff, clamp: true }
        : { ...config.default, clamp: false },
      opacity: 1,
      onStart: {
        y: () => setV(true),
      },
    });
  }, [api]);

  const close = useCallback(() => {
    api.start({
      y: getHeight(),
      immediate: false,
      config: isMobile ? config.stiff : config.slow,
      opacity: 0,
      onRest: {
        y: () => setV(false),
      },
    });
  }, [api, setV]);

  const display = y.to((py) => (isV && py < getHeight() ? 'flex' : 'none'));

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
      open();
    } else {
      document.body.style.removeProperty('overflow');
      close();
    }
  }, [close, isVisible, open]);

  return (
    <>
      <animated.div
        className={cx(modalClassName, {
          [modalScrollableStyle]: isScrollable,
          [className]: !!className,
        })}
        style={{
          display,
          bottom: `calc(-100vh + ${getHeight() - 100}px)`,
          y,
          opacity,
        }}
      >
        {isV && (
          <div
            className={cx(containerClassName, {
              [containerScrollableStyle]: isScrollable,
            })}
            ref={containerRef}
          >
            {children}
          </div>
        )}
      </animated.div>
      <Overlay isVisible={isVisible} />
    </>
  );
};

Modal.displayName = 'Modal';

export default Modal;
